import { gql, useLazyQuery, useMutation } from '@apollo/client'
import Close from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import Grow from '@mui/material/Grow'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {
  Brand,
  Maybe,
  MutationBatchCreateBrandRequestsArgs,
  Query,
} from '@promoboxx/graphql-gateway-types'
import { Field, Formik } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import NotifyPromoboxxButton from '@src/lib/NotifyPromoboxxButton'
import partition from '@src/lib/partition'
import useModalState from '@src/lib/useModalState'
import {
  brandIdsForCurrentRetailerAccountSelector,
  brandsForCurrentRetailerAccountSelector,
} from '@src/stores/app/selectors'

import IndustrySelectableBrand from './IndustrySelectableBrand'
import manageBrandsPaths from './manageBrandsPaths'
import SuggestBrandsCard from './SuggestBrandsCard'

const GET_BRANDS_FOR_INDUSTRY = gql`
  query getBrandsForIndustryList($industry_list: [String!]!) {
    getBrandsForIndustryList(
      industry_list: $industry_list
      filters: { status: active, brand_requests_enabled: true, demo: false }
    ) {
      id
      name
      logo_url
      community
    }
  }
`

const GET_INDUSTRY_LIST = gql`
  query getIndustryList($id: ID!) {
    getRetailerAccount(id: $id) {
      id
      potential_industry_list
    }
  }
`

const BATCH_CREATE = gql`
  mutation batchCreateBrandRequests(
    $brand_ids: [ID!]!
    $brand_request: CreateBrandRequestBrandRequest!
  ) {
    batchCreateBrandRequests(
      brand_ids: $brand_ids
      brand_request: $brand_request
    ) {
      id
    }
  }
`

const useStyles = makeStyles()((theme) => ({
  picBrands: {
    background: theme.palette.grey[200],
    marginBottom: theme.spacing(4),
  },
  requestAccessButton: {
    flex: '0 0 130px',
  },
  requestButtonSticky: {
    position: 'sticky',
    // Our navbar is 66px tall.
    // Have to use calc since `theme.spacing` returns a CSS unit.
    top: `calc(66px + ${theme.spacing(2)})`,
    // We don't want this container to interfere with user interaction.
    pointerEvents: 'none',
  },
  requestButtonContainer: {
    // This is to mask disabled buttons having an opacity.
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    // Allow interaction on the actual button.
    pointerEvents: 'all',
    display: 'inline-block',
  },
}))

interface InYourIndustryProps {
  isLoading?: boolean
  ignoreBrandIds?: Maybe<Brand['id'] | undefined>[]
  onDidRequest?: () => void
}

const InYourIndustry: React.FC<React.PropsWithChildren<InYourIndustryProps>> = (
  props,
) => {
  const { classes } = useStyles()
  const retailerAccountBrandIds = useSelector<unknown, number[]>(
    brandIdsForCurrentRetailerAccountSelector,
  )
  const retailerAccountBrands = useSelector<unknown, Brand[]>(
    brandsForCurrentRetailerAccountSelector,
  )

  const showBrandRequests = retailerAccountBrands.every((brand) => {
    return (
      brand?.settings
        ?.retailer_connection_requests_industry_suggestions_enabled === true
    )
  })

  const shouldShowPic = retailerAccountBrands.every((brand) => {
    return (
      brand?.settings?.retailer_connection_requests_pic_suggestions_enabled ===
      true
    )
  })

  const params = manageBrandsPaths.index.useParams()
  const [getBrandsForIndustry, { data: brandsData, loading: isLoadingBrands }] =
    useLazyQuery<Query>(GET_BRANDS_FOR_INDUSTRY)
  const [
    getIndustryList,
    { data: industryListData, loading: isLoadingIndustryList },
  ] = useLazyQuery<Query>(GET_INDUSTRY_LIST)
  const [selectedBrands, setSelectedBrands] = useState<Brand['id'][]>([])

  function toggleBrand(id: Brand['id']) {
    return setSelectedBrands((ids) => toggleInList(ids, id))
  }
  const isLoading = props.isLoading || isLoadingBrands || isLoadingIndustryList
  const brands = useMemo(() => {
    const brands = (brandsData?.getBrandsForIndustryList || []).filter(
      (brand) => {
        return ![...retailerAccountBrandIds, ...(props.ignoreBrandIds || [])]
          .map(String)
          .includes(String(brand.id))
      },
    )
    brands.sort((a, b) => {
      const aName = a.name
      const bName = b.name
      if (aName && bName) {
        return aName.localeCompare(bName)
      }
      return 0
    })
    return brands
  }, [
    brandsData?.getBrandsForIndustryList,
    retailerAccountBrandIds,
    props.ignoreBrandIds,
  ])

  const [picBrands, nonPicBrands] = partition(
    brands,
    (brand) => brand.community === true,
  )

  // The In Your Industry section has some complex logic around when it should
  // be shown.
  const shouldShowInYourIndustry =
    showBrandRequests &&
    // We need to show it while loading, as it contains the loading indicator.
    (isLoading ||
      // Show the section when there are non-PIC brands.
      (!isLoading && nonPicBrands.length > 0) ||
      // Show the section when NO brands are found, so the user gets access to the
      // "Notify Promoboxx" button.
      (!isLoading && brands.length === 0))

  const { enqueueSnackbar } = useSnackbar()
  const [batchCreateBrandRequests] = useMutation<
    Query,
    MutationBatchCreateBrandRequestsArgs
  >(BATCH_CREATE)
  const { isOpen, closeModal, openModal } = useModalState()
  const {
    isOpen: isSuggestOpen,
    closeModal: closeSuggestModal,
    openModal: openSuggestModal,
  } = useModalState()
  const resetFormRef = useRef<() => void>()
  const resetAndCloseModal = () => {
    resetFormRef.current?.()
    closeModal()
  }

  useEffect(() => {
    setSelectedBrands([])
    getIndustryList({ variables: { id: params.retailerAccountSlug } })
    resetAndCloseModal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.retailerAccountSlug])

  useEffect(() => {
    getBrandsForIndustry({
      variables: {
        industry_list:
          industryListData?.getRetailerAccount?.potential_industry_list || [],
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryListData?.getRetailerAccount?.potential_industry_list])

  return (
    <div>
      <Box textAlign="right" mb={2} className={classes.requestButtonSticky}>
        <span className={classes.requestButtonContainer}>
          <Button
            color="primary"
            variant="contained"
            size="large"
            disabled={selectedBrands.length === 0}
            onClick={openModal}
            style={{
              position: 'sticky',
              top: 0,
            }}
          >
            {I18n.t('manageBrands.requestTheseBrands', {
              count: selectedBrands.length,
            })}
          </Button>
        </span>
      </Box>

      {shouldShowPic && picBrands.length > 0 && (
        <Paper className={classes.picBrands}>
          <Box padding={4}>
            <Typography
              variant="h6"
              style={{ whiteSpace: 'nowrap', width: '300px' }}
            >
              {I18n.t('manageBrands.industryContent')}
            </Typography>

            <Typography color="textSecondary">
              {I18n.t('manageBrands.industryContentDescription')}{' '}
            </Typography>
          </Box>
          <Box padding={4}>
            <Grid container spacing={4}>
              {picBrands.map((brand) => {
                return (
                  <Grid item xs={6} md={4} lg={2} key={brand.id}>
                    <IndustrySelectableBrand
                      brand={brand}
                      isSelected={selectedBrands.includes(brand.id)}
                      onClick={(brand) => toggleBrand(brand.id)}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </Paper>
      )}
      {shouldShowInYourIndustry && (
        <Paper>
          <Box display="flex" alignItems="center" padding={4}>
            <Typography variant="h6" style={{ flex: '1 1 auto' }}>
              {I18n.t('manageBrands.inYourIndustry')}
            </Typography>
          </Box>

          {!isLoading && brands.length === 0 && (
            <Box padding={4}>
              <div style={{ textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {I18n.t('manageBrands.noneInIndustry')}
                </Typography>

                <NotifyPromoboxxButton
                  color="primary"
                  size="large"
                  variant="contained"
                  data={{
                    industryList:
                      industryListData?.getRetailerAccount
                        ?.potential_industry_list,
                  }}
                />
              </div>
            </Box>
          )}
          <Box padding={4}>
            <Grid container spacing={4}>
              {nonPicBrands.map((brand) => {
                return (
                  <Grid item xs={6} md={4} lg={2} key={brand.id}>
                    <IndustrySelectableBrand
                      brand={brand}
                      isSelected={selectedBrands.includes(brand.id)}
                      onClick={(brand) => toggleBrand(brand.id)}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Box>

          {isLoading && <LinearProgress />}
        </Paper>
      )}

      <Formik
        initialValues={{ retailer_message: '' }}
        onSubmit={async (values, formik) => {
          try {
            await batchCreateBrandRequests({
              variables: {
                brand_ids: selectedBrands,
                brand_request: {
                  retailer_account_id: params.retailerAccountSlug,
                  retailer_account_message: values.retailer_message,
                  source: 'discovered',
                },
              },
            })

            enqueueSnackbar(
              I18n.t('manageBrands.requestAccessSuccessMessage'),
              { variant: 'success' },
            )

            resetAndCloseModal()
            setSelectedBrands([])
            openSuggestModal()
          } catch (err) {
            console.error(err)
            enqueueSnackbar(I18n.t('manageBrands.requestAccessErrorMessage'), {
              variant: 'error',
            })
          }

          formik.setSubmitting(false)
          props.onDidRequest?.()
        }}
      >
        {({ submitForm, isSubmitting, resetForm }) => {
          resetFormRef.current = resetForm
          return (
            <Dialog
              open={isOpen && selectedBrands.length > 0}
              onClose={resetAndCloseModal}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>{I18n.t('manageBrands.requestAccess')}</DialogTitle>
              <DialogContent>
                <Field
                  name="retailer_message"
                  label={I18n.t('manageBrands.requestAccessMessage')}
                  helperText={I18n.t(
                    'manageBrands.requestAccessMessageHelperText',
                  )}
                  component={TextField}
                  fullWidth
                  rows={3}
                  rowsMax={6}
                  multiline
                  autoFocus
                />
              </DialogContent>
              <DialogActions>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="div"
                >
                  {I18n.t('manageBrands.informationDisclosureMessage', {
                    buttonLabel: I18n.t('manageBrands.requestAccess'),
                  })}
                </Typography>
                <Button onClick={resetAndCloseModal}>
                  {I18n.t('shared.cancel')}
                </Button>
                <Button
                  className={classes.requestAccessButton}
                  color="primary"
                  variant="contained"
                  onClick={submitForm}
                  disabled={isSubmitting}
                >
                  {I18n.t('manageBrands.requestAccess')}
                </Button>
              </DialogActions>
            </Dialog>
          )
        }}
      </Formik>
      <Dialog
        open={isSuggestOpen}
        onClose={closeSuggestModal}
        TransitionComponent={Grow}
        // Normally we prefer scroll="paper", but this dialog doesn't really
        // follow the title/content/actions pattern.
        scroll="body"
      >
        <DialogContent style={{ padding: '0' }}>
          <SuggestBrandsCard
            topContent={
              <Box display="flex" alignItems="top">
                <Box flexGrow="1">
                  <Typography variant="h6" paragraph>
                    {I18n.t('manageBrands.postDiscoverSuggestBrandsTitle')}
                  </Typography>
                </Box>
                <div>
                  <IconButton onClick={closeSuggestModal} size="large">
                    <Close />
                  </IconButton>
                </div>
              </Box>
            }
            onDidSubmit={() => {
              enqueueSnackbar(
                I18n.t('manageBrands.suggestBrandsPage.requestSent'),
                { variant: 'success' },
              )
              // closeSuggestModal()
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}

function toggleInList<T>(arr: T[], id: T) {
  if (arr.includes(id)) {
    return arr.filter((x) => x !== id)
  }

  return arr.concat(id)
}

export default InYourIndustry
